import { useSyncDictionaryWithContext } from '@/shared/lib/i18n';
import { UserFeatureToggles } from 'clients/UserClient/types';
import { useUpdateCurrentUserFeatureTogglesMutation } from 'hooks/shared/useUpdateCurrentUserFeatureTogglesMutation';
import { createContext, useEffect, useState } from 'react';
import { useBrowserExtensionLocalStorage } from 'shared/legacy/hooks/useBrowserExtension/useBrowserExtensionLocalStorage';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import { useLocalizationStore } from 'stores/localization/localization.store';
import { dictionary as en } from './languages/en';
import { dictionary as nl } from './languages/nl';
import { ContextProps, LocalizationContextProviderProps, SupportedLanguage, SupportedLocale } from './types';

const initialLanguage: SupportedLanguage =
  window.navigator.language.startsWith('en') || window.location.href.includes('localhost') ? 'en' : 'nl';

const defaultContext: ContextProps = {
  language: initialLanguage,
  locale: initialLanguage === 'en' ? 'en-US' : 'nl-NL',
  dictionary: initialLanguage === 'en' ? en : nl,
  setLanguage: () => null,
};

export const LocalizationContext = createContext(defaultContext);

export const LocalizationContextProvider = ({ children }: LocalizationContextProviderProps) => {
  const browserExtensionLS = useBrowserExtensionLocalStorage();
  const setDictionary = useLocalizationStore((state) => state.setDictionary);
  const [language, setLanguage] = useState<SupportedLanguage>(
    (localStorage.getItem('languageV2') as SupportedLanguage) || defaultContext.language,
  );
  const dictionaries = { nl, en };
  const dictionary = dictionaries[language];
  const { mutate: updateLanguage } = useUpdateCurrentUserFeatureTogglesMutation();
  const { data: currentUser } = useCurrentUserQuery();

  const locale: SupportedLocale = language === 'en' ? 'en-US' : 'nl-NL';

  useEffect(() => {
    if (currentUser?.preferredLanguage) {
      setLanguage(currentUser.preferredLanguage);
    }
  }, [currentUser?.preferredLanguage]);

  useEffect(() => {
    browserExtensionLS
      .get<SupportedLanguage>('languageV2')
      .then((data) => data && data !== language && setLanguage(data));
  }, [browserExtensionLS.get]);

  useEffect(() => {
    if (currentUser) {
      updateLanguage([
        {
          name: UserFeatureToggles.SELECTED_LANGUAGE,
          value: language,
          enabled: true,
        },
      ]);
    }
    browserExtensionLS.set('languageV2', language);
    localStorage.setItem('languageV2', language);
  }, [language, browserExtensionLS.set, currentUser, updateLanguage]);

  useEffect(() => {
    setDictionary(dictionary);
  }, [dictionary, setDictionary]);

  useSyncDictionaryWithContext(dictionary, language);

  return (
    <LocalizationContext.Provider
      value={{
        locale,
        language,
        dictionary,
        setLanguage,
      }}>
      {children}
    </LocalizationContext.Provider>
  );
};
